import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import commonEn from './locales/en/common.json';
import commonRu from './locales/ru/common.json';
import shellEn from './locales/en/shell.json';
import shellRu from './locales/ru/shell.json';
import dataEn from './locales/en/data.json';
import dataRu from './locales/ru/data.json';
import assistantEn from './locales/en/assistant.json';
import assistantRu from './locales/ru/assistant.json';
import metadataEn from './locales/en/metadata.json';
import metadataRu from './locales/ru/metadata.json';
import connectionsEn from './locales/en/connections.json';
import connectionsRu from './locales/ru/connections.json';
import connectionGuideEn from './locales/en/connectionGuide.json';
import connectionGuideRu from './locales/ru/connectionGuide.json';

export const LANGUAGE_STORAGE_KEY = 'evolve-language';
export const SUPPORTED_LANGUAGES = ['en', 'ru'];
export const SYSTEM_LANGUAGE = 'system';

export function normalizeLanguage(value) {
  const code = typeof value === 'string' ? value.toLowerCase().split(/[-_]/)[0] : '';
  return SUPPORTED_LANGUAGES.includes(code) ? code : null;
}

/** The operating system's language, as the browser reports it. */
export function systemLanguage() {
  const languages = typeof navigator === 'undefined' ? [] : navigator.languages || [navigator.language];
  return languages.map(normalizeLanguage).find(Boolean) || 'en';
}

export function detectLanguage() {
  // The cookie is shared with Django's account/registration/MFA pages.
  try {
    const cookie = document.cookie.split('; ').find((item) => item.startsWith('django_language='));
    if (cookie) {
      try {
        const saved = normalizeLanguage(decodeURIComponent(cookie.split('=')[1]));
        if (saved) return saved;
      } catch (_) { /* Ignore malformed preferences from older versions. */ }
    }
    const stored = normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
    if (stored) return stored;
  } catch (_) { /* Private browsing can disable storage. */ }
  return systemLanguage();
}

/** `ru`/`en` once chosen; `system` while the console follows the OS. */
export function languagePreference() {
  try {
    const cookie = document.cookie.split('; ').find((item) => item.startsWith('django_language='));
    return normalizeLanguage(cookie && decodeURIComponent(cookie.split('=')[1]))
      || normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY)) || SYSTEM_LANGUAGE;
  } catch (_) {
    return SYSTEM_LANGUAGE;
  }
}

// Bundled catalogs work offline and never send product copy or user data to a TMS.
const resources = {
  en: { translation: { common: commonEn, shell: shellEn, data: dataEn, assistant: assistantEn, metadata: metadataEn, connections: connectionsEn, connectionGuide: connectionGuideEn } },
  ru: { translation: { common: commonRu, shell: shellRu, data: dataRu, assistant: assistantRu, metadata: metadataRu, connections: connectionsRu, connectionGuide: connectionGuideRu } },
};

i18n.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  supportedLngs: SUPPORTED_LANGUAGES,
  fallbackLng: 'en',
  initImmediate: false,
  returnNull: false,
  returnEmptyString: false,
  interpolation: { escapeValue: false },
  // A damaged/unreleased key must never become visible technical UI text.
  parseMissingKeyHandler: () => (i18n.resolvedLanguage === 'ru' ? 'Текст недоступен' : 'Text unavailable'),
});

function applyLanguage(language) {
  const code = normalizeLanguage(language) || 'en';
  if (typeof document !== 'undefined') document.documentElement.lang = code;
}
applyLanguage(i18n.resolvedLanguage);
i18n.on('languageChanged', applyLanguage);

// While following the system, a change of the OS language applies at once.
if (typeof window !== 'undefined') {
  window.addEventListener('languagechange', () => {
    if (languagePreference() === SYSTEM_LANGUAGE) i18n.changeLanguage(systemLanguage());
  });
}

export async function changeLanguage(language) {
  if (language === SYSTEM_LANGUAGE) {
    try { localStorage.removeItem(LANGUAGE_STORAGE_KEY); } catch (_) { /* storage optional */ }
    if (typeof document !== 'undefined') document.cookie = 'django_language=; Path=/; Max-Age=0; SameSite=Lax';
    await i18n.changeLanguage(systemLanguage());
    return;
  }
  const code = normalizeLanguage(language);
  if (!code) return;
  // Stored first: the switch re-renders whatever reads the preference.
  try { localStorage.setItem(LANGUAGE_STORAGE_KEY, code); } catch (_) { /* storage optional */ }
  if (typeof document !== 'undefined') {
    document.cookie = `django_language=${code === 'en' ? 'en-us' : code}; Path=/; Max-Age=31536000; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  }
  await i18n.changeLanguage(code);
}

export const t = (...args) => i18n.t(...args);
export default i18n;
